import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, ShieldCheck, AlertTriangle, MessageSquare, Send,
  Clock, User, Loader2, ExternalLink,
} from "lucide-react";

/**
 * iter356.A — Public phenotype-share page.
 *
 * URL: /share/phenotype/:token
 * No authentication required. Read-only view of the patient's endocrine
 * signature snapshot. Clinicians can append a note.
 *
 * Endpoints:
 *   GET  /api/share/phenotype/{token}
 *   POST /api/share/phenotype/{token}/note
 */

const CONF_LABELS = {
  high:     { color: "#10B981", bg: "Висока достоверност" },
  moderate: { color: "#F59E0B", bg: "Умерена достоверност" },
  low:      { color: "#EF4444", bg: "Ниска достоверност" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("bg-BG", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function BlendBar({ blend }) {
  if (!blend) return null;
  return (
    <div className="space-y-1.5">
      {blend.slice(0, 5).map((p) => (
        <div key={p.key}>
          <div className="flex items-center justify-between text-sm mb-0.5">
            <span className="text-gray-700">{p.label_bg}</span>
            <span className="font-bold" style={{ color: p.color }}>{Math.round((p.weight || 0) * 100)}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full" style={{
              width: `${Math.round((p.weight || 0) * 100)}%`,
              backgroundColor: p.color,
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PhenotypeSharePage() {
  const { token } = useParams();
  const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [credentials, setCredentials] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/share/phenotype/${token}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Линкът не може да бъде зареден");
    } finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitNote = async () => {
    if (!name.trim() || !note.trim()) {
      toast.warning("Името и бележката са задължителни");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/share/phenotype/${token}/note`, {
        clinician_name: name.trim(),
        credentials: credentials.trim(),
        note: note.trim(),
      });
      toast.success("Бележката е изпратена на пациента");
      setNote("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при изпращане");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="text-xl font-bold text-gray-800">Линкът не е достъпен</h1>
            <p className="text-sm text-gray-600">{error}</p>
            <a href="/" className="text-indigo-500 text-sm underline flex items-center justify-center gap-1">
              <ExternalLink className="w-4 h-4" /> Към Nutramilo
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  const syn = data?.synthesis || {};
  const dom = syn.dominant_phenotype || {};
  const conf = CONF_LABELS[syn.confidence] || CONF_LABELS.low;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">

        {/* Banner — patient identity */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="rounded-full bg-indigo-500/10 p-3">
                <User className="w-6 h-6 text-indigo-500" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  Ендокринен профил на пациент
                </p>
                <h1 className="text-xl font-extrabold text-gray-800" data-testid="share-patient-name">
                  {data.patient_display_name}
                </h1>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] text-gray-500">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Споделено: {fmtDate(data.shared_at)}</span>
              <span>•</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Изтича: {fmtDate(data.expires_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Dominant phenotype hero */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: dom.color || "#6366F1" }} />
                <h2 className="text-base font-bold text-gray-800">Доминиращ ендокринен сигнал</h2>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ color: conf.color, backgroundColor: `${conf.color}20` }}
                    data-testid="share-confidence">{conf.bg}</span>
            </div>
            <div className="rounded-xl p-4 mb-4"
                 style={{
                   background: `linear-gradient(135deg, ${dom.color}15, ${dom.color}05)`,
                   border: `1px solid ${dom.color}30`,
                 }}
                 data-testid="share-dominant">
              <p className="text-3xl font-extrabold text-gray-800">{dom.label_bg}</p>
              <p className="text-xs mt-1 text-gray-500">
                Тегло на сигнала: {Math.round((dom.weight || 0) * 100)}% · базирано на {syn.modules_used || 0} модула
              </p>
            </div>

            <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">
              Разпределение на сигнала
            </h3>
            <BlendBar blend={syn.phenotype_blend} />

            {syn.drivers_top?.length > 0 && (
              <>
                <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2 mt-4">
                  Какво стимулира профила
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {syn.drivers_top.slice(0, 4).map((d) => (
                    <span key={`driver-${d.name}`}
                          className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                      {d.name}
                    </span>
                  ))}
                </div>
              </>
            )}

            {syn.recommendations_bg?.length > 0 && (
              <div className="mt-4 rounded-lg p-3 bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                    Автоматични препоръки
                  </p>
                </div>
                <ul className="space-y-1.5">
                  {syn.recommendations_bg.slice(0, 4).map((r) => (
                    <li key={`rec-${r.slice(0, 32)}`} className="text-[11px] leading-snug flex items-start gap-2 text-gray-700">
                      <span className="text-emerald-500 mt-0.5">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Existing notes */}
        {data.notes?.length > 0 && (
          <Card className="border-0 shadow-md">
            <CardContent className="p-5">
              <h2 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-indigo-500" />
                Бележки от лекари ({data.notes.length})
              </h2>
              <ul className="space-y-3" data-testid="share-notes-list">
                {data.notes.map((n, i) => (
                  <li key={n._id || n.id || `${n.clinician_name}-${n.created_at}`}
                      className="rounded-lg p-3 bg-indigo-50 border border-indigo-100"
                      data-testid={`note-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-bold text-gray-800">{n.clinician_name}</p>
                      <p className="text-[10px] text-gray-500">{fmtDate(n.created_at)}</p>
                    </div>
                    {n.credentials && (
                      <p className="text-[11px] text-gray-500 mb-1.5 italic">{n.credentials}</p>
                    )}
                    <p className="text-[12px] text-gray-700 leading-snug whitespace-pre-wrap">{n.note}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Add note form (clinician input) */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Добави препоръка</h2>
            <p className="text-[11px] text-gray-500 mb-3">
              Като лекар или близък, оставете бележка тук. Пациентът ще я види в приложението си.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <Input
                placeholder="Вашето име *"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                data-testid="share-note-name-input"
              />
              <Input
                placeholder="Специалност / квалификация (по избор)"
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
                maxLength={100}
                data-testid="share-note-credentials-input"
              />
            </div>
            <Textarea
              placeholder="Препоръка към пациента…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={5}
              maxLength={4000}
              className="mb-2"
              data-testid="share-note-text-input"
            />
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-gray-400">{note.length}/4000</p>
              <Button
                onClick={submitNote}
                disabled={submitting}
                className="gradient-primary text-white"
                data-testid="share-note-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Изпращам…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Изпрати препоръка</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-[10px] text-gray-400 pt-2 pb-4">
          Защитен с подписан JWT · Бележките не могат да бъдат редактирани (audit trail).
          <br />
          <a href="/" className="text-indigo-500 underline mt-1 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Към Nutramilo
          </a>
        </div>
      </div>
    </div>
  );
}
